import { useSelector } from "react-redux";
import type { ReactNode } from "react";
import { Card } from "../../../../../core/components/Card";
import { Column } from "../../../../../core/components/Column";
import { Row } from "../../../../../core/components/Row";
import { TextComponent } from "../../../../../core/components/TextComponent";
import { ArrowRight } from "../../../../../core/icons/ArrowRight";
import { EmailBlack } from "../../../../../core/icons/Email";
import { Profile } from "../../../../../core/icons/Profile";
import { RootState } from "../../../../../core/store/store";
import { Pencil } from "../../../../../core/icons/Pencil";
import { useState } from "react";
import { EditProfile } from "../../components/EditProfile";
import { CheckVerify } from "../../../../../core/icons/CheckVerify";
import { ModalSimple } from "../../../../../core/components/Modal";
import { CustomButton } from "../../../../../core/components/CustomButton";
import { useNavigate } from "react-router-dom";
import { CheckVerifyBG } from "../../../../../core/icons/CheckVerifyBG";
import { useSubscription } from "../../../../subscriptions/presentation/hook/useSuscription";
import { Settings } from "../../../../../core/icons/Settings";
import { XClose } from "../../../../../core/icons/XClose";
import { Friends } from "../../../../../core/icons/Friends";
import { Benefits } from "../../../../../core/icons/Benefits";
import { isAffiliateCountryAllowed } from "../../../../settings/presentation/utils/affiliateCountries";

const RelationshipPrioritiesIcon = () => (
  <svg
    className="h-7 w-7"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    aria-hidden="true"
  >
    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8l1.1 1.1L12 21l7.8-7.5 1.1-1.1a5.5 5.5 0 0 0-.1-7.8Z" />
  </svg>
);

const ProfileAction = ({
  title,
  icon,
  onClick,
  iconColor = "text-[#777777]",
  selected = false,
  selectedColor = "bg-primary",
  className = "",
}: {
  title: string;
  icon: ReactNode;
  onClick: () => void;
  iconColor?: string;
  selected?: boolean;
  selectedColor?: string;
  className?: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex min-w-0 flex-col items-center text-center active:scale-95 transition-transform ${className}`}
  >
    <span
      className={`flex h-[62px] w-[62px] items-center justify-center rounded-full shadow-[0px_1px_4px_rgba(128,128,128,0.3)] [&_svg]:h-[30px] [&_svg]:w-[30px] ${
        selected ? `${selectedColor} text-white` : `bg-white ${iconColor}`
      }`}
    >
      {icon}
    </span>
    <span className="mt-2 max-w-full text-center text-[16px] font-medium leading-tight text-graySecondary line-clamp-2">
      {title}
    </span>
  </button>
);

export const ProfileScreen = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.onboarding);
  const { subscriptionsByUser, isLoadingSubscriptionByUser } =
    useSubscription();
  const [isEditProfile, setIsEditProfile] = useState(false);
  const { getProfileConfig } = useSelector(
    (state: RootState) => state.onboarding,
  );
  const [statusModalOpen, setStatusModalOpen] = useState<boolean>(false);

  const status = getProfileConfig?.status_account;
  const isLiveFaceRequired = status === "live_face_required";
  const showAffiliateProgram = isAffiliateCountryAllowed(user?.country);

  const handleEditProfile = () => {
    setIsEditProfile(!isEditProfile);
  };

  const handleVerifyProfile = () => {
    if (status === "pending") {
      setStatusModalOpen(true);
    } else if (
      status === null ||
      status === "rejected" ||
      status === "live_face_required"
    ) {
      navigate("/settings/statusVerification");
    }
  };

  return (
    <>
      <EditProfile
        hiddenEditProfile={isEditProfile}
        handleEditProfile={handleEditProfile}
      />
      <Column
        crossAxisAlignment="start"
        className="bg-white p-4 gap-4 md:gap-5 max-md:overflow-y-auto"
      >
        <div className="md:hidden flex w-full flex-col items-center pt-1">
          <TextComponent variant="h2" className="mb-5 text-center text-[22px] font-bold">
            Profile
          </TextComponent>
          <Profile
            src={user?.profile_pictures?.[0]?.url || "image/default"}
            alt={user?.username}
            className="h-[175px] w-[175px] text-white"
            strokeWidth={0}
            size={175}
          />
          <TextComponent variant="h2" className="mt-5 text-center text-[24px] font-bold leading-tight">
            {user?.username}
          </TextComponent>
          <div className="mt-[10px] grid w-full grid-cols-3 items-start gap-0">
            <ProfileAction
              title="Setting"
              icon={<Settings />}
              iconColor="text-[#777777]"
              onClick={() => navigate("/settings")}
            />
            <ProfileAction
              title={
                status === "verified"
                  ? "Verified"
                  : isLiveFaceRequired
                    ? "Live face"
                    : "Not verified"
              }
              icon={status === "verified" ? <CheckVerify color="white" /> : <XClose />}
              onClick={handleVerifyProfile}
              selected
              selectedColor={status === "verified" ? "bg-[#22C55E]" : "bg-red-600"}
              className="pt-8"
            />
            <ProfileAction
              title="Edit Info"
              icon={<Pencil />}
              iconColor="text-[#777777]"
              onClick={handleEditProfile}
            />
          </div>
        </div>
        <div className="relative w-full max-md:hidden">
          <img
            className="rounded-lg h-36 object-cover w-full select-none"
            src="fotoPortada.png"
            draggable={false}
            alt="user-portada"
          />
          <div className="flex h-12 justify-between">
            <Profile
              src={user?.profile_pictures?.[0]?.url || "image/default"}
              alt={user?.username}
              className="relative w-32 h-32 text-white left-10 bottom-[5rem] max-md:left-1/2 max-md:transform max-md:-translate-x-1/2"
              strokeWidth={15}
              size={128}
            />
            <button
              type="button"
              className="transition-colors duration-300 ease-in-out hover:text-primary p-2 rounded-md select-none"
              onClick={handleEditProfile}
            >
              <Row className="gap-2">
                <Pencil />
                <TextComponent variant="h5" className="font-semibold">
                  Edit Profile
                </TextComponent>
              </Row>
            </button>
          </div>
        </div>
        <Column className="w-full lg:px-10 gap-4 md:gap-5">
          <Column className="relative" crossAxisAlignment="start">
            <Row className="gap-2 mb-5 md:gap-3 max-md:hidden" crossAxisAlignment="center">
              <Column>
                <TextComponent variant="h1" className="text-center">
                  {user?.username}
                </TextComponent>
              </Column>
              {status === "verified" ? (
                <CheckVerify size={30} />
              ) : (
                <button
                  type="button"
                  onClick={handleVerifyProfile}
                  className="flex items-center gap-2 text-graySecondary hover:text-primary transition-colors duration-300 ease-in-out"
                >
                  <CheckVerify color="#c5c5c5" size={30} />
                  <TextComponent variant="h5" className="font-semibold">
                    {isLiveFaceRequired
                      ? "Live face check required"
                      : "Not verified"}
                  </TextComponent>
                </button>
              )}
            </Row>
            {status !== "verified" && (
              <Column className="gap-2 md:gap-3 md:w-[28rem]">
                <TextComponent variant="h5" className="font-semibold">
                  {isLiveFaceRequired
                    ? "Your account is suspended until you complete a live face check."
                    : "Get verified and receive 5 premium instant messages!"}
                </TextComponent>
                <CustomButton type="button" onClick={handleVerifyProfile}>
                  {status === "pending"
                    ? "Verification under review"
                    : isLiveFaceRequired
                      ? "Complete live face check"
                      : "Get Verified"}
                </CustomButton>
                <button
                  type="button"
                  onClick={() => navigate("/profile/instant-message-info")}
                  className="w-fit text-primary font-semibold hover:underline"
                >
                  More info
                </button>
              </Column>
            )}
            <Column className="gap-10 md:gap-14 w-full lg:w-[40rem]">
              <Column className="gap-4 md:gap-5">
                <Row className="gap-4 md:gap-5 w-full max-md:flex-col">
                  <Card
                    title="Email Address"
                    className="md:w-1/2"
                    text={user?.email}
                    icon={<EmailBlack />}
                  />
                </Row>
                <Column className="gap-4 md:gap-5">
                  <button
                    type="button"
                    onClick={() => navigate("/profile/relationship-priorities")}
                    className="flex w-full select-none items-center gap-3 rounded-2xl border border-black/10 bg-white px-4 py-3.5 text-left hover:bg-primary/5"
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                      <RelationshipPrioritiesIcon />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-base font-bold text-black">
                        Relationship Priorities
                      </span>
                      <span className="mt-0.5 block text-sm leading-snug text-graySecondary">
                        Optional values for more meaningful introductions
                      </span>
                    </span>
                    <ArrowRight />
                  </button>
                  <Card
                    title="Community"
                    text="Share feedback, ideas, and approved stories."
                    variant="pink"
                    icon={<Friends />}
                    icon2={<ArrowRight />}
                    onClick={() => navigate("/community")}
                    className="select-none"
                  />
                  {showAffiliateProgram && (
                    <Card
                      title="Affiliate program"
                      text="Share your link and track your earnings."
                      variant="pink"
                      icon={<Benefits />}
                      icon2={<ArrowRight />}
                      onClick={() => navigate("/settings/affiliate")}
                      className="select-none"
                    />
                  )}
                  <Card
                    title={
                      subscriptionsByUser
                        ? `Active ${subscriptionsByUser.subscription_duration}-Month Subscription`
                        : "Upgrade Now!"
                    }
                    text={
                      subscriptionsByUser
                        ? ``
                        : "See who likes you and get more ways to connect!"
                    }
                    variant="pink"
                    icon={
                      <img
                        className="w-16 aspect-square"
                        draggable={false}
                        src="card.svg"
                        alt="card"
                      />
                    }
                    icon2={<ArrowRight />}
                    onClick={() => navigate("/subscription")}
                    className={`${isLoadingSubscriptionByUser && "hidden"} select-none`}
                  />
                </Column>
              </Column>
            </Column>
          </Column>
        </Column>
      </Column>
      <ModalSimple
        title="Information"
        variant="secondary"
        actionExecuted={statusModalOpen}
        onClose={() => {
          setStatusModalOpen(false);
        }}
        icon={<CheckVerifyBG />}
        next
      >
        {status === "pending" ? (
          <TextComponent variant="h5">
            Thanks for submitting your photo! Please allow up to 24 hours for
            our staff to manually verify your profile.
          </TextComponent>
        ) : status === "live_face_required" ? (
          <TextComponent variant="h5">
            Your account is suspended until you complete a live face check in
            the Android app.
          </TextComponent>
        ) : (
          <TextComponent variant="h5">
            Your profile is already verified
          </TextComponent>
        )}
      </ModalSimple>
    </>
  );
};
